/**
 * Chat — Compliance Threads (Palantir edition).
 *
 * PII-redacted async messaging between platform users.  Sidebar of
 * threads, main chat pane, "new thread" modal.  Same backend
 * endpoints — /chat/threads, /chat/messages, /chat/threads — just
 * dressed in the Bloomberg / Palantir aesthetic with bracketed
 * headers, monospace timestamps, and pure-violet message bubbles
 * for the active user.
 */
import React, { useEffect, useRef, useState } from 'react';
import { MessagesSquare, Send, Plus, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { CHAT } from '@/constants/testIds';

export default function Chat() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [subject, setSubject] = useState('Partnership discussion');
  const [otherId, setOtherId] = useState('');
  const endRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get('/chat/threads');
    setThreads(data);
    if (data[0]) setActive(data[0]);
  };
  const loadMessages = async (t) => {
    if (!t) return;
    const { data } = await api.get(`/chat/messages/${t.thread_id}`);
    setMessages(data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on plain async fetches */
  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { loadMessages(active); }, [active?.thread_id]);

  useEffect(() => {
    if (newOpen) {
      api.get('/users/directory').then(r => setUsers(r.data.filter(u => u.user_id !== user?.user_id)));
    }
  }, [newOpen, user]);
  /* eslint-enable */

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post('/chat/messages', { thread_id: active.thread_id, text });
      setMessages(m => [...m, data]);
      setText('');
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      if (data.redacted) toast.warning('PII detected and redacted — only text allowed in compliance chat.');
    } catch { toast.error('Failed to send'); }
  };

  const createThread = async (e) => {
    e.preventDefault();
    if (!otherId) return toast.error('Pick a participant');
    try {
      const { data } = await api.post('/chat/threads', { other_user_id: otherId, subject });
      setNewOpen(false);
      await loadThreads();
      setActive(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create');
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="pal-card pal-grid-bg p-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="pal-bracket">[COMPLIANCE CHAT · PII-REDACTED]</div>
          <h1 className="display text-2xl md:text-3xl mt-1.5">Talk freely. PII stays off the wire.</h1>
          <p className="pal-mono text-[11px] mt-1.5 opacity-70 max-w-2xl">
            Emails, phones, links and social handles are auto-redacted in real time.
          </p>
        </div>
        <button
          onClick={() => setNewOpen(true)}
          data-testid={CHAT.newBtn}
          className="self-start shrink-0 px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold rounded pal-mono transition-transform hover:translate-y-[-1px] bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)] flex items-center gap-1.5"
        >
          <Plus size={12} /> NEW · THREAD
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[640px]">
        {/* Thread sidebar */}
        <aside className="lg:col-span-4 pal-card pal-grid-bg p-3 overflow-y-auto">
          <div className="pal-bracket text-[9px] mb-2 px-1">[THREADS · N={threads.length}]</div>
          {threads.length === 0 && (
            <div className="text-center pal-mono text-xs opacity-55 p-8">
              NO THREADS YET
            </div>
          )}
          <div className="space-y-1">
            {threads.map(t => {
              const isActive = active?.thread_id === t.thread_id;
              return (
                <button
                  key={t.thread_id}
                  onClick={() => setActive(t)}
                  className={`w-full text-left p-2.5 rounded border transition-all ${
                    isActive
                      ? 'border-[var(--ve-violet)] bg-[rgba(174,82,234,0.10)]'
                      : 'border-[rgba(174,82,234,0.15)] hover:border-[var(--ve-violet)]/50 hover:bg-[rgba(174,82,234,0.04)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MessagesSquare size={12} className="text-[var(--ve-violet-bright)] shrink-0" />
                    <span className="pal-mono text-[11px] font-bold truncate">{t.subject}</span>
                  </div>
                  <div className="pal-mono text-[9px] opacity-55 mt-1 ml-5 tabular-nums">
                    {new Date(t.last_message_at).toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main chat pane */}
        <main className="lg:col-span-8 pal-card pal-grid-bg flex flex-col">
          {!active && (
            <div className="flex-1 flex items-center justify-center pal-mono text-xs opacity-55">
              SELECT OR START A THREAD
            </div>
          )}
          {active && (
            <>
              <div className="p-4 border-b border-[rgba(174,82,234,0.18)] flex items-center justify-between">
                <div className="min-w-0">
                  <div className="pal-bracket text-[9px]">[SUBJECT]</div>
                  <div className="text-sm font-semibold mt-0.5 truncate">{active.subject}</div>
                </div>
                <div className="flex items-center gap-1.5 pal-status pal-status-violet shrink-0">
                  <ShieldAlert size={11} /> PII · AUTO-REDACT
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map(m => {
                  const mine = m.sender_user_id === user?.user_id;
                  return (
                    <div
                      key={m.message_id}
                      className={`max-w-[80%] p-3 rounded text-sm ${
                        mine
                          ? 'bg-[var(--ve-violet)] text-black ml-auto border border-[var(--ve-violet-bright)]/30'
                          : 'border border-[rgba(174,82,234,0.18)] bg-[rgba(174,82,234,0.04)]'
                      }`}
                    >
                      <div className={`pal-mono text-[9px] uppercase tracking-[0.18em] font-bold mb-1 ${mine ? 'opacity-70' : 'opacity-55'}`}>
                        {mine ? '[YOU]' : '[PEER]'}
                      </div>
                      <div>{m.text}</div>
                      {m.redacted && (
                        <div className={`pal-mono text-[9px] mt-1.5 ${mine ? 'opacity-70' : 'opacity-55'}`}>
                          ⚠ PII · REDACTED
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-[rgba(174,82,234,0.18)] flex items-end gap-2">
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  rows={1}
                  data-testid={CHAT.input}
                  placeholder="Type your message…"
                  className="flex-1 rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2.5 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)] resize-none"
                />
                <button
                  onClick={send}
                  data-testid={CHAT.send}
                  className="shrink-0 px-3 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded pal-mono bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)] flex items-center gap-1.5"
                >
                  <Send size={12} /> SEND
                </button>
              </div>
            </>
          )}
        </main>
      </div>

      {/* New-thread modal */}
      {newOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && setNewOpen(false)}
        >
          <form onSubmit={createThread} className="pal-card pal-grid-bg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <span className="pal-bracket">[NEW · COMPLIANCE THREAD]</span>
              <button
                type="button"
                onClick={() => setNewOpen(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <label className="block">
              <span className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 font-bold">PARTICIPANT</span>
              <select
                required
                value={otherId}
                onChange={e => setOtherId(e.target.value)}
                className="mt-1.5 w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2.5 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]"
              >
                <option value="">— Choose a user —</option>
                {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.role}</option>)}
              </select>
            </label>
            <label className="mt-4 block">
              <span className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 font-bold">SUBJECT</span>
              <input
                required
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="mt-1.5 w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2.5 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]"
              />
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setNewOpen(false)}
                className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold rounded pal-mono border border-[rgba(174,82,234,0.30)] hover:bg-[rgba(174,82,234,0.06)]"
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-bold rounded pal-mono bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)]"
              >
                START · THREAD →
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
